import { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';

// material-ui
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

// ant design icons
import DownOutlined from '@ant-design/icons/DownOutlined';
import EditOutlined from '@ant-design/icons/EditOutlined';
import PlusOutlined from '@ant-design/icons/PlusOutlined';
import RightOutlined from '@ant-design/icons/RightOutlined';
import SearchOutlined from '@ant-design/icons/SearchOutlined';

// project imports
import { getIepVersion, updateIepVersion } from 'services/api/iep';
import usePageAccess from 'hooks/usePageAccess';
import useOnlineStatus from 'hooks/useOnlineStatus';
import BehaviorEditorDialog from 'sections/shared/child-detail/BehaviorEditorDialog';
import { behaviorKey } from 'services/clinicalDatasheets';
import { findDuplicateBehavior, otherBehaviorNames } from 'utils/behaviorNames';

const ROWS_PER_PAGE_OPTIONS = [5, 10, 25];

const newBehaviorId = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `beh-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

/**
 * Session Shortcut -> Behaviors. The child's behaviors of concern for the IEP version
 * the session's data sheets read (paginated, searchable), with Add and Edit through
 * the SAME BehaviorEditorDialog the child's Behaviors tab uses, so a behavior added
 * mid-session is identical to one added there and lands in the same snapshot.
 *
 * Writes go to iep_version.snapshot_data.behaviors exactly like BehaviorsTab: the
 * snapshot is re-read right before the save and only `behaviors` is replaced, so
 * other IEP fields (and edits made elsewhere since the dialog opened) survive.
 *
 * Every behavior saved here carries a stable `id`. The tracking sheets key recorded
 * occurrences by that id (falling back to index + name), so a new behavior gets its
 * own key and an edited one keeps the key it already had - counts recorded earlier
 * in this session stay attached after a rename.
 *
 * Needs the network (it writes the IEP), like Add Target. Offline it says so.
 */
const SessionBehaviorsDialog = ({ open, onClose, versionId = '', onChanged }) => {
  const intl = useIntl();
  const theme = useTheme();
  // Phones / portrait tablets: one expandable card per behavior (like the target
  // list) instead of the table, and no pagination. Phones get the dialog full screen.
  const isCompact = useMediaQuery(theme.breakpoints.down('md'));
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  // Behavior keys whose card is expanded (shows the full definition).
  const [openCards, setOpenCards] = useState({});
  const { isOnline } = useOnlineStatus();
  const { readOnly: isCodeReadOnly } = usePageAccess();
  const readOnly = isCodeReadOnly('child_behaviors');

  const [behaviors, setBehaviors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(ROWS_PER_PAGE_OPTIONS[0]);
  // null = closed; { index: null } = adding; { index: n } = editing behaviors[n].
  const [editor, setEditor] = useState(null);

  const load = useCallback(async () => {
    if (!versionId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await getIepVersion(versionId);
      setBehaviors(Array.isArray(res?.snapshot_data?.behaviors) ? res.snapshot_data.behaviors : []);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to load behaviors');
      setBehaviors([]);
    } finally {
      setLoading(false);
    }
  }, [versionId]);

  useEffect(() => {
    if (!open) return;
    setSearch('');
    setPage(0);
    setNotice(null);
    setEditor(null);
    setOpenCards({});
    if (isOnline) load();
  }, [open, isOnline, load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rows = behaviors.map((b, index) => ({ b, index }));
    if (!q) return rows;
    return rows.filter(({ b }) => `${b.behaviorName || ''} ${b.operationalDefinition || ''}`.toLowerCase().includes(q));
  }, [behaviors, search]);

  const pageRows = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  // Keep the page in range when a search narrows the list.
  useEffect(() => {
    if (page > 0 && page * rowsPerPage >= filtered.length) setPage(0);
  }, [filtered.length, page, rowsPerPage]);

  const handleSave = async (draft) => {
    const editingIndex = editor?.index ?? null;
    const original = editingIndex !== null ? behaviors[editingIndex] : null;
    setSaving(true);
    setError(null);
    try {
      // Re-read so a concurrent edit to the IEP (or to these behaviors) is not
      // overwritten by the copy loaded when the dialog opened.
      const fresh = await getIepVersion(versionId);
      const snapshot = fresh?.snapshot_data || {};
      const current = Array.isArray(snapshot.behaviors) ? snapshot.behaviors : [];

      let next;
      if (original) {
        // Find the row being edited in the FRESH list: by id, else by the key the
        // sheets derived for it (index + name), which also confirms it did not move.
        const key = behaviorKey(original, editingIndex);
        const at = current.findIndex((b, i) => behaviorKey(b, i) === key);
        if (at < 0) {
          setError(
            intl.formatMessage({
              id: 'sessionBehaviors.changedElsewhere',
              defaultMessage: 'This behavior was changed elsewhere. The list has been reloaded - please try again.'
            })
          );
          setBehaviors(current);
          setEditor(null);
          return;
        }
        // Pin the key the sheets already use so a rename keeps this session's counts.
        next = current.map((b, i) => (i === at ? { ...draft, id: key } : b));
      } else {
        next = [...current, { ...draft, id: newBehaviorId() }];
      }

      // Re-check against the FRESH list: someone may have added the same name since.
      const dupe = findDuplicateBehavior(
        draft.behaviorName,
        current,
        original ? current.findIndex((b, i) => behaviorKey(b, i) === behaviorKey(original, editingIndex)) : null
      );
      if (dupe) {
        setError(
          intl.formatMessage(
            {
              id: 'behaviorsTab.duplicateName',
              defaultMessage: '"{name}" already exists for this child. Use a different name or edit the existing behavior.'
            },
            { name: dupe.behaviorName || dupe.name }
          )
        );
        setBehaviors(current);
        return;
      }

      const res = await updateIepVersion(versionId, { snapshot_data: { ...snapshot, behaviors: next } });
      setBehaviors(Array.isArray(res?.snapshot_data?.behaviors) ? res.snapshot_data.behaviors : next);
      setEditor(null);
      if (!original) {
        setSearch('');
        // Jump to the last page so the new behavior is in view.
        setPage(Math.max(0, Math.ceil(next.length / rowsPerPage) - 1));
      }
      setNotice(
        intl.formatMessage(
          original
            ? { id: 'sessionBehaviors.updated', defaultMessage: '"{name}" updated.' }
            : { id: 'sessionBehaviors.added', defaultMessage: '"{name}" added - it is now available on the behavior sheets.' },
          { name: draft.behaviorName }
        )
      );
      // Let the session reload its clinical data so the sheets pick it up.
      if (onChanged) await onChanged();
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to save behavior');
    } finally {
      setSaving(false);
    }
  };

  const measurementChips = (b) => {
    const m = b.measurement || {};
    return [
      m.trackFrequency && intl.formatMessage({ id: 'iep.pbsp.behavior.frequency' }),
      m.trackDuration && intl.formatMessage({ id: 'iep.pbsp.behavior.duration' }),
      m.trackIntensity && intl.formatMessage({ id: 'iep.pbsp.behavior.intensity' })
    ].filter(Boolean);
  };

  const canEdit = !readOnly && isOnline && Boolean(versionId);

  const editButton = (index) =>
    !readOnly && (
      <Tooltip title={intl.formatMessage({ id: 'behaviorsTab.editBehavior' })}>
        <span>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setEditor({ index });
            }}
            disabled={!canEdit || saving}
            aria-label={intl.formatMessage({ id: 'behaviorsTab.editBehavior' })}
          >
            <EditOutlined />
          </IconButton>
        </span>
      </Tooltip>
    );

  // Compact list: a card per behavior; tap to expand the operational definition.
  const cardList = (
    <Stack spacing={1}>
      {filtered.map(({ b, index }) => {
        const key = behaviorKey(b, index);
        const isOpen = Boolean(openCards[key]);
        const chips = measurementChips(b);
        const hasDetails = Boolean(b.operationalDefinition);
        const toggle = () => hasDetails && setOpenCards((m) => ({ ...m, [key]: !m[key] }));
        return (
          <Card key={key} variant="outlined" sx={{ overflow: 'hidden' }}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              onClick={toggle}
              role={hasDetails ? 'button' : undefined}
              aria-expanded={hasDetails ? isOpen : undefined}
              sx={{ px: 1.5, py: 1.25, cursor: hasDetails ? 'pointer' : 'default' }}
            >
              <Box sx={{ width: 16, flexShrink: 0, color: 'text.secondary', display: 'flex' }}>
                {hasDetails && (isOpen ? <DownOutlined style={{ fontSize: 12 }} /> : <RightOutlined style={{ fontSize: 12 }} />)}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle2" sx={{ wordBreak: 'break-word' }}>
                  {b.behaviorName || intl.formatMessage({ id: 'behaviorsTab.unnamed' })}
                </Typography>
                {chips.length > 0 && (
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                    {chips.map((label) => (
                      <Chip key={label} size="small" variant="outlined" label={label} />
                    ))}
                  </Stack>
                )}
              </Box>
              {editButton(index)}
            </Stack>
            {hasDetails && (
              <Collapse in={isOpen} timeout="auto" unmountOnExit>
                <Box sx={{ px: 1.5, pb: 1.5, pl: 5 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
                    {intl.formatMessage({ id: 'sessionBehaviors.definition', defaultMessage: 'Operational definition' })}
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {b.operationalDefinition}
                  </Typography>
                </Box>
              </Collapse>
            )}
          </Card>
        );
      })}
    </Stack>
  );

  return (
    <>
      <Dialog open={open && !editor} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth fullScreen={isPhone}>
        <DialogTitle>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="h5">{intl.formatMessage({ id: 'behaviorsTab.trackedBehaviors' })}</Typography>
              {!loading && <Chip size="small" color="primary" label={behaviors.length} />}
            </Stack>
            {!readOnly && (
              <Button
                variant="contained"
                size="small"
                startIcon={<PlusOutlined />}
                onClick={() => setEditor({ index: null })}
                disabled={!canEdit || loading || saving}
              >
                {intl.formatMessage({ id: 'behaviorsTab.addBehavior' })}
              </Button>
            )}
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            {!isOnline && (
              <Alert severity="warning">
                {intl.formatMessage({
                  id: 'sessionBehaviors.offline',
                  defaultMessage: 'Behaviors can be added or edited only while online. Data entry on the sheets keeps working offline.'
                })}
              </Alert>
            )}
            {isOnline && !versionId && (
              <Alert severity="info">
                {intl.formatMessage({ id: 'sessionBehaviors.noIep', defaultMessage: 'This child has no active IEP to add behaviors to.' })}
              </Alert>
            )}
            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
            {notice && (
              <Alert severity="success" onClose={() => setNotice(null)}>
                {notice}
              </Alert>
            )}

            {isOnline && versionId && (
              <>
                <TextField
                  size="small"
                  fullWidth
                  placeholder={intl.formatMessage({ id: 'sessionBehaviors.search', defaultMessage: 'Search behaviors' })}
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(0);
                  }}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchOutlined />
                        </InputAdornment>
                      )
                    }
                  }}
                />

                {loading ? (
                  <Stack alignItems="center" sx={{ py: 4 }}>
                    <CircularProgress size={28} />
                  </Stack>
                ) : filtered.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                    {search
                      ? intl.formatMessage({ id: 'sessionBehaviors.noMatch', defaultMessage: 'No behaviors match your search.' })
                      : intl.formatMessage({ id: 'behaviorsTab.noBehaviorsInVersion' })}
                  </Typography>
                ) : isCompact ? (
                  cardList
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>{intl.formatMessage({ id: 'sessionBehaviors.colBehavior', defaultMessage: 'Behavior' })}</TableCell>
                          <TableCell>
                            {intl.formatMessage({ id: 'sessionBehaviors.colMeasurement', defaultMessage: 'Measurement' })}
                          </TableCell>
                          {!readOnly && <TableCell align="right" />}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {pageRows.map(({ b, index }) => (
                          <TableRow key={behaviorKey(b, index)} hover>
                            <TableCell sx={{ maxWidth: 360 }}>
                              <Typography variant="subtitle2">
                                {b.behaviorName || intl.formatMessage({ id: 'behaviorsTab.unnamed' })}
                              </Typography>
                              {b.operationalDefinition && (
                                <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                                  {b.operationalDefinition}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                {measurementChips(b).map((label) => (
                                  <Chip key={label} size="small" variant="outlined" label={label} />
                                ))}
                              </Stack>
                            </TableCell>
                            {!readOnly && <TableCell align="right">{editButton(index)}</TableCell>}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                {filtered.length > 0 && !isCompact && (
                  <TablePagination
                    component="div"
                    count={filtered.length}
                    page={page}
                    onPageChange={(_, p) => setPage(p)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => {
                      setRowsPerPage(parseInt(e.target.value, 10));
                      setPage(0);
                    }}
                    rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                  />
                )}
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button onClick={onClose} disabled={saving}>
            {intl.formatMessage({ id: 'common.close' })}
          </Button>
        </DialogActions>
      </Dialog>

      {/* The Behaviors tab's own Add / Edit dialog. */}
      <BehaviorEditorDialog
        open={open && Boolean(editor)}
        initial={editor && editor.index !== null ? behaviors[editor.index] : null}
        saving={saving}
        existingNames={otherBehaviorNames(behaviors, editor ? editor.index : null)}
        onClose={() => !saving && setEditor(null)}
        onSave={handleSave}
      />
    </>
  );
};

SessionBehaviorsDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  // The IEP version the session's sheets read (clinical bundle's active version).
  versionId: PropTypes.string,
  // Called after a successful save; the session reloads its clinical data.
  onChanged: PropTypes.func
};

export default SessionBehaviorsDialog;
